

import React from 'react';
import { motion, Variants, easeOut } from 'framer-motion'; // Essential for animations
import { Link } from 'react-router-dom';

// --- Animation Variants (Refined for a professional feel) ---
export const fadeIn: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.42, 0, 1, 1], // ✅ easeOut equivalent
    },
  },
};

export const slideIn = (direction: "left" | "right"): Variants => ({
  hidden: {
    opacity: 0,
    x: direction === "left" ? -50 : 50,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0.42, 0, 1, 1], // ✅
    },
  },
});

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

export const itemScaleUp: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6 },
  },
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.3,
      ease: [0.42, 0, 1, 1],
    },
  },
};

export const highlightFade: Variants = {
  hidden: { opacity: 0, borderBottomWidth: 0 },
  visible: {
    opacity: 1,
    borderBottomWidth: 2,
    transition: {
      duration: 0.5,
      ease: [0.42, 0, 1, 1],
    },
  },
};


const LegalProcessPage: React.FC = () => {
  return (
    <>
      {/* --- SEO Head Section (for Pages Router) --- */}
      {/* If using App Router, ensure the 'metadata' object is exported above and remove this <Head> component. */}
      
        <title>Expert Legal Process Services | Nadezhda Venture | Noida, India</title>
        <meta
          name="description"
          content="Navigate legal complexities with precision. [Your Company Name] offers comprehensive LPO solutions: document review, contract management, litigation support, and compliance for businesses in Noida & globally."
        />
        <link rel="canonical" href="https://yourwebsite.com/legal-process" />
        <meta property="og:title" content="Expert Legal Process Services | Nadezhda Venture" />
        <meta property="og:description" content="Specialized Legal Process Outsourcing (LPO) for corporate and legal entities." />
        <meta property="og:url" content="https://yourwebsite.com/legal-process" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://yourwebsite.com/images/og-legal-process.jpg" /> {/* Placeholder */}
        <meta property="og:image:alt" content="Legal Process Solutions" />
       

      <div className="legal-process-page min-h-screen bg-gray-50 text-gray-800 font-sans">

        {/* 1. Hero Section: Authority & Clarity */}
        <motion.section
          className="relative h-[450px] flex items-center justify-center text-center bg-black text-white overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <div className="absolute inset-0 z-0">
            <img
              src="https://img.freepik.com/free-photo/front-view-blurry-lawyer-working_23-2151202429.jpg?semt=ais_hybrid&w=740" // High-res image: law books, abstract courthouse, scales of justice, professional office
              alt="Professional legal setting with law books and scales of justice"
              className="opacity-80 w-full h-full object-cover absolute inset-0"
              loading="eager"
            />
            <div className="absolute inset-0 bg-black opacity-60"></div> {/* Darker overlay for contrast */}
          </div>
          <div className="z-10 max-w-6xl px-6">
            <motion.h1
              className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 tracking-tight drop-shadow-md text-orange-500"
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              Legal Processes Outsourcing
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl mb-10 opacity-90 max-w-4xl mx-auto font-light"
              variants={fadeIn}
              transition={{ delay: 0.5 }}
            >
             Our area of expertise is offering knowledgeable virtual legal assistant services tailored to the particular requirements of individual attorneys, corporate legal departments, and law firms.
            </motion.p>
            <motion.button
              // className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-3 px-10 rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 text-lg"
              variants={fadeIn}
              transition={{ delay: 0.8 }}
              whileHover={{ scale: 1.05, boxShadow: '0 8px 16px rgba(0,0,0,0.3)' }}
              whileTap={{ scale: 0.95 }}
              aria-label="Schedule a legal consultation"
            >
             <Link
    to="/contact"
    className="inline-block bg-blue-800 hover:bg-blue-900 text-white font-semibold py-3 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
  >
     Schedule a Consultation
  </Link>
             
            </motion.button>
          </div>
        </motion.section>

  

        {/* 2. The Imperative of Legal Precision */}
        <motion.section
          className="py-16 px-8 bg-white shadow-inner"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={staggerContainer}
        >
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <motion.div variants={slideIn('left')}>
              <h2 className="text-4xl font-bold mb-6 text-blue-800 border-b-4 border-orange-500 pb-2 inline-block">
                Legal Support
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Improve your legal operations with help of our dedicated virtual legal support specialist. From document preparation to conducting research, their accuracy and efficiency is unparalleled, making our specialist different.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Virtual Legal Assistants Via their expertise in legal research, document making ready, and field management, they assist your follow with accuracy and pace. Automate your legal processes, improve the quality of customer service, and secure a place.
              </p>
            </motion.div>
            <motion.div variants={slideIn('right')} className="flex justify-center">
              <img
                src="https://nvassistant.com/Assets/allimages/legal/legal%20process%20image.png" // Icon representing precision, focus, or compliance
                alt="Abstract icon representing legal precision and focus"
                className="w-full max-w-sm h-auto opacity-80"
                loading="lazy"
              />
            </motion.div>
          </div>
        </motion.section>

    

        {/* 3. Our Core Legal Process Services (Service Grid - Clean & Direct) */}
        <motion.section
          className="py-16 px-8 bg-blue-50"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.h2
            className="text-4xl font-bold text-center mb-12 text-blue-800"
            variants={fadeIn}
          >
            Our Core Legal Process Services
          </motion.h2>
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1: Document Review */}
            <motion.div
              className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-orange-600 flex flex-col items-center text-center hover:shadow-xl transition-shadow duration-300"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-search text-6xl text-blue-700 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Document Review & E-Discovery</h3>
              <p className="text-lg text-gray-700">
                Meticulous and technology-driven review for litigation, regulatory inquiries, and due diligence. Includes responsiveness, privilege, and PII review.
              </p>
            </motion.div>

            {/* Service Card 2: Contract Management */}
            <motion.div
              className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-orange-600 flex flex-col items-center text-center hover:shadow-xl transition-shadow duration-300"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-file-contract text-6xl text-green-700 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Contract Lifecycle Management</h3>
              <p className="text-lg text-gray-700">
                End-to-end support from drafting and negotiation to abstraction, compliance, and obligation tracking.
              </p>
            </motion.div>

            {/* Service Card 3: Legal Research */}
            <motion.div
              className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-orange-600 flex flex-col items-center text-center hover:shadow-xl transition-shadow duration-300"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-balance-scale text-6xl text-purple-700 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Legal Research & Analysis</h3>
              <p className="text-lg text-gray-700">
                In-depth legal research, statutory analysis, case law summaries, and opinion drafting to support strategic decisions.
              </p>
            </motion.div>

            {/* Service Card 4: Litigation Support */}
            <motion.div
              className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-orange-600 flex flex-col items-center text-center hover:shadow-xl transition-shadow duration-300"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-gavel text-6xl text-red-700 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Litigation & Discovery Support</h3>
              <p className="text-lg text-gray-700">
                Assistance with discovery requests, witness preparation, exhibit management, and pre-trial documentation.
              </p>
            </motion.div>

            {/* Service Card 5: Compliance */}
            <motion.div
              className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-orange-600 flex flex-col items-center text-center hover:shadow-xl transition-shadow duration-300"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-clipboard-check text-6xl text-orange-700 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Regulatory Compliance & Advisory</h3>
              <p className="text-lg text-gray-700">
                Ensuring adherence to industry-specific regulations, data privacy laws, and corporate governance standards.
              </p>
            </motion.div>

            {/* Service Card 6: IP Support */}
            <motion.div
              className="bg-white rounded-lg shadow-lg p-8 border-t-4 border-orange-600 flex flex-col items-center text-center hover:shadow-xl transition-shadow duration-300"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-lightbulb text-6xl text-teal-700 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Intellectual Property Services</h3>
              <p className="text-lg text-gray-700">
                Support for patent research, trademark registration, copyright filings, and IP portfolio management.
              </p>
            </motion.div>
          </div>
        </motion.section>

    
        {/* 4. Our Commitment: Pillars of Your Legal Success */}
        <motion.section
          className="py-16 px-8 bg-orange-200 text-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={staggerContainer}
        >
          <motion.h2
            className="text-4xl font-bold text-center mb-12 text-blue-800"
            variants={fadeIn}
          >
            Our Commitment: Pillars of Your Legal Success
          </motion.h2>
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10">
            {/* Pillar 1: Expertise */}
            <motion.div
              className="flex flex-col items-center text-center bg-blue-700 p-8 rounded-lg shadow-xl border-b-4 border-orange-600"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-university text-7xl text-teal-300 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3">Unparalleled Expertise</h3>
              <p className="text-gray-200">
                Our team comprises seasoned legal professionals, ensuring top-tier knowledge and execution in every task.
              </p>
            </motion.div>

            {/* Pillar 2: Security */}
            <motion.div
              className="flex flex-col items-center text-center bg-blue-700 p-8 rounded-lg shadow-xl border-b-4 border-orange-600"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-shield-alt text-7xl text-green-300 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3">Ironclad Data Security</h3>
              <p className="text-gray-200">
                Strict adherence to global data protection standards and robust security protocols safeguard your sensitive information.
              </p>
            </motion.div>

            {/* Pillar 3: Efficiency */}
            <motion.div
              className="flex flex-col items-center text-center bg-blue-700 p-8 rounded-lg shadow-xl border-b-4 border-orange-600"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <i className="fas fa-stopwatch text-7xl text-purple-300 mb-6"></i>
              <h3 className="text-2xl font-bold mb-3">Operational Efficiency</h3>
              <p className="text-gray-200">
                Streamlined processes and technology integration ensure timely delivery and optimized resource utilization.
              </p>
            </motion.div>
          </div>
        </motion.section>

        <motion.section
  className="py-16 px-8 bg-gray-100"
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true, amount: 0.3 }}
  variants={staggerContainer}
>
  <motion.h2
    className="text-4xl font-bold text-center mb-12 text-blue-800"
    variants={fadeIn}
  >
    Our Seamless Process: From Engagement to Execution
  </motion.h2>
  <div className="max-w-4xl mx-auto space-y-8">
    {[
      { icon: "fas fa-handshake", title: "Initial Consultation & Scoping", description: "Understanding your precise legal requirements and defining the project scope." },
      { icon: "fas fa-cogs", title: "Strategy & Resource Allocation", description: "Developing a tailored approach and deploying specialized legal talent and technology." },
      { icon: "fas fa-clipboard-check", title: "Execution & Quality Assurance", description: "Rigorous execution with multi-tiered quality control to ensure accuracy." },
      { icon: "fas fa-file-export", title: "Reporting & Delivery", description: "Providing clear, concise reports and timely delivery of completed legal tasks." },
    ].map((step, index) => (
      <motion.div
        key={index}
        className="flex items-start bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500 hover:shadow-lg transition-shadow duration-300"
        variants={slideIn(index % 2 === 0 ? 'left' : 'right')}
      >
        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-blue-600 text-white flex flex-col items-center justify-center text-3xl font-bold shadow-lg mr-6">
          <span className="text-xl leading-none">{index + 1}</span> {/* Added number here */}
          <i className={`${step.icon} text-2xl`}></i> {/* Adjusted icon size */}
        </div>
        <div>
          <h3 className="text-xl font-bold mb-2 text-gray-900">{step.title}</h3>
          <p className="text-gray-700">{step.description}</p>
        </div>
      </motion.div>
    ))}
  </div>
</motion.section>


        {/* 6. Client Testimonials (Focused on Professional Impact) */}
        {/* <motion.section
          className="py-16 px-8 bg-blue-900 text-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.h2
            className="text-4xl font-bold text-center mb-12"
            variants={fadeIn}
          >
            Trusted by Leading Legal Departments & Firms
          </motion.h2>
          <div className="max-w-4xl mx-auto space-y-12">
           
            <motion.div
              className="bg-white p-8 rounded-lg shadow-xl text-gray-800 border-l-4 border-teal-500"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <p className="text-lg italic mb-4 leading-relaxed">
                &quot;The precision and speed with which **[Your Company Name]** handles our document review requirements are exceptional. Their commitment to data security and legal accuracy is paramount, making them an indispensable partner for our corporate legal function.&quot;
              </p>
              <div className="flex items-center mt-6">
                <img
                  src="/images/avatar-official-client-1.jpg" // Professional avatar placeholder
                  alt="Avatar of Corporate Legal Head"
                  className="w-16 h-16 rounded-full mr-4 border-2 border-teal-500"
                  loading="lazy"
                />
                <div>
                  <p className="font-semibold text-blue-700">Dr. Anya Sharma</p>
                  <p className="text-gray-600 text-sm">Head of Legal, Global Pharma Corp.</p>
                </div>
              </div>
            </motion.div>

            
            <motion.div
              className="bg-white p-8 rounded-lg shadow-xl text-gray-800 border-l-4 border-green-500"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <p className="text-lg italic mb-4 leading-relaxed">
                &quot;For complex legal research and compliance audits, **[Your Company Name]** provides unparalleled support. Their insights are always precise, and their process is incredibly efficient. A true asset to our practice.&quot;
              </p>
              <div className="flex items-center mt-6">
                <img
                  src="/images/avatar-official-client-2.jpg" // Professional avatar placeholder
                  alt="Avatar of Managing Partner Law Firm"
                  className="w-16 h-16 rounded-full mr-4 border-2 border-green-500"
                  loading="lazy"
                />
                <div>
                  <p className="font-semibold text-blue-700">Adv. Rajeev Kapoor</p>
                  <p className="text-gray-600 text-sm">Managing Partner, Kapoor & Partners Law</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section> */}

        {/* 7. Frequently Asked Questions (FAQ) - Retaining efficient dropdown */}
        <motion.section
          className="py-16 px-8 bg-gray-50"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.h2
            className="text-4xl font-bold text-center mb-12 text-blue-800"
            variants={fadeIn}
          >
            Frequently Asked Questions
          </motion.h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <motion.details
              className="bg-white p-6 rounded-lg shadow-md cursor-pointer border border-gray-200"
              variants={itemScaleUp}
              whileHover="hover"
            >
              <summary className="text-xl font-semibold text-gray-900 flex justify-between items-center py-1">
                What distinguishes your Legal Process Services?
                <i className="fas fa-chevron-down text-blue-900">+</i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed border-t border-gray-100 pt-4">
                Our distinction lies in our blend of deep legal expertise, advanced technology, and stringent data security protocols**. We offer tailor-made solutions, ensuring not just compliance but also strategic advantage through efficient process management.
              </p>
            </motion.details>

            <motion.details
              className="bg-white p-6 rounded-lg shadow-md cursor-pointer border border-gray-200"
              variants={itemScaleUp}
              transition={{ delay: 0.1 }}
              whileHover="hover"
            >
              <summary className="text-xl font-semibold text-gray-900 flex justify-between items-center py-1">
                How do you ensure confidentiality and intellectual property protection?
                <i className="fas fa-chevron-down text-blue-900">+</i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed border-t border-gray-100 pt-4">
                We prioritize **client confidentiality** through robust security frameworks, **ISO 27001-compliant practices**, secure data transfer, and comprehensive **Non-Disclosure Agreements (NDAs)** with all personnel. Your intellectual property is protected with the highest standards.
              </p>
            </motion.details>

            <motion.details
              className="bg-white p-6 rounded-lg shadow-md cursor-pointer border border-gray-200"
              variants={itemScaleUp}
              transition={{ delay: 0.2 }}
              whileHover="hover"
            >
              <summary className="text-xl font-semibold text-gray-900 flex justify-between items-center py-1">
                Are your services compliant with international legal standards and jurisdictions?
                <i className="fas fa-chevron-down text-blue-900">+</i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed border-t border-gray-100 pt-4">
                Yes, our team is proficient in handling legal processes across **multiple jurisdictions**, including US, UK, EU, and APAC regions. Our processes are designed to align with international best practices and regulatory requirements. Based in **Noida, India**, we provide a competitive global delivery model.
              </p>
            </motion.details>

            <motion.details
              className="bg-white p-6 rounded-lg shadow-md cursor-pointer border border-gray-200"
              variants={itemScaleUp}
              transition={{ delay: 0.3 }}
              whileHover="hover"
            >
              <summary className="text-xl font-semibold text-gray-900 flex justify-between items-center py-1">
                What are the typical cost savings associated with LPO?
                <i className="fas fa-chevron-down text-blue-900">+</i>
              </summary>
              <p className="text-gray-700 mt-4 leading-relaxed border-t border-gray-100 pt-4">
                Engaging in LPO can lead to significant cost reductions, often ranging from **30% to 50%**, compared to in-house legal operations. This is achieved through optimized resource utilization, lower operational overheads, and efficient process management.
              </p>
            </motion.details>
          </div>
        </motion.section>

        {/* JSON-LD Schema for FAQ Page - Critical for SEO Rich Snippets */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What distinguishes your Legal Process Services?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Our distinction lies in our blend of deep legal expertise, advanced technology, and stringent data security protocols. We offer tailor-made solutions, ensuring not just compliance but also strategic advantage through efficient process management."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do you ensure confidentiality and intellectual property protection?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We prioritize client confidentiality through robust security frameworks, ISO 27001-compliant practices, secure data transfer, and comprehensive Non-Disclosure Agreements (NDAs) with all personnel. Your intellectual property is protected with the highest standards."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Are your services compliant with international legal standards and jurisdictions?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, our team is proficient in handling legal processes across multiple jurisdictions, including US, UK, EU, and APAC regions. Our processes are designed to align with international best practices and regulatory requirements. Based in Noida, India, we provide a competitive global delivery model."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What are the typical cost savings associated with LPO?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Engaging in LPO can lead to significant cost reductions, often ranging from 30% to 50%, compared to in-house legal operations. This is achieved through optimized resource utilization, lower operational overheads, and efficient process management."
                  }
                }
              ]
            }
          `}
        </script>

      
        {/* 8. Final Call to Action: Authoritative & Clear */}
        <motion.section
          className="py-20 px-8 bg-gradient-to-r from-blue-900 to-indigo-950 text-white text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true,  }}
          variants={fadeIn}
        >
          <motion.h2
            className="text-3xl md:text-4xl font-extrabold mb-6 drop-shadow-md text-orange-500"
            variants={fadeIn}
            transition={{ delay: 0.2 }}
          >
            Elevate Your Legal Operations with Confidence
          </motion.h2>
          <motion.p
            className="text-xl md:text-2xl mb-10 opacity-90 max-w-3xl mx-auto font-light"
            variants={fadeIn}
            transition={{ delay: 0.4 }}
          >
            Partner with [ Nadezhda Venture Pvt. Ltd.] for unparalleled legal process support that delivers precision, efficiency, and peace of mind.
          </motion.p>
          <motion.button
            // className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-4 px-12 rounded-full shadow-xl transform transition duration-300 ease-in-out hover:scale-105 text-xl"
            variants={fadeIn}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.05, boxShadow: '0 12px 24px rgba(0,0,0,0.3)' }}
            whileTap={{ scale: 0.95 }}
            aria-label="Request a detailed proposal for legal services"
          >
            <Link
    to="/contact"
    className="inline-block bg-orange-700 hover:bg-orange-500 text-white font-semibold py-3 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
  >
     Request a Detailed Proposal
  </Link>
            
          </motion.button>
        </motion.section>

      </div>
    </>
  );
};

export default LegalProcessPage;